import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { get } from "@/lib/api";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { StatusChip } from "@/components/app/StatusChip";
import { DeliveryStatusCard, RetryFailedButton } from "@/components/app/EmailAlerts";

export default function OutboxSettings() {
  const { data, isLoading } = useQuery({ queryKey: ["outbox"], queryFn: () => get("/settings/email/outbox"), refetchInterval: 5000 });
  const [open, setOpen] = useState(null);
  if (isLoading) return <SkeletonRows rows={5} />;
  if (!data.length) return <div className="space-y-4"><DeliveryStatusCard /><EmptyState testId="outbox-empty" icon={Mail} title="No emails yet" text="Password resets, test emails and candidate invitations will be listed here." /></div>;
  return (
    <div className="space-y-4">
    <DeliveryStatusCard />
    <section className="panel overflow-hidden max-lg:overflow-x-auto" data-testid="outbox-settings">
      <div className="flex items-center gap-3 border-b border-line px-6 py-4"><h2 className="flex-1 text-lg font-semibold">Email outbox</h2><RetryFailedButton /></div>
      <table className="w-full text-sm" data-testid="outbox-table">
        <thead className="bg-subtle text-left text-xs text-ink-muted">
          <tr><th className="px-6 py-3 font-medium">To</th><th className="px-4 py-3 font-medium">Subject</th><th className="px-4 py-3 font-medium">Status</th><th className="px-6 py-3 text-right font-medium">Created</th></tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.map((m) => (
            <tr key={m.id} onClick={() => setOpen(m)} onKeyDown={(e) => e.key === "Enter" && setOpen(m)} tabIndex={0} className="tl-row" data-testid={`outbox-row-${m.id}`}>
              <td className="px-6 py-3">{m.to}</td>
              <td className="px-4 py-3"><p className="text-ink">{m.subject}</p><p className="text-xs text-ink-muted">{m.kind.replace("_", " ")}</p></td>
              <td className="px-4 py-3"><StatusChip status={m.status} /><p className="mt-1 text-xs text-ink-muted" data-testid={`outbox-detail-${m.id}`}>{m.status_detail}</p></td>
              <td className="px-6 py-3 text-right text-xs text-ink-muted">{format(new Date(m.created_at), "d MMM yyyy, HH:mm")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-2xl rounded-[6px]" data-testid="outbox-preview-dialog">
          <DialogHeader><DialogTitle>{open?.subject}</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-secondary">To {open?.to} · {open?.status_detail || open?.status}</p>
          <iframe title="Email preview" sandbox="" srcDoc={open?.html} className="h-80 w-full rounded-lg border border-line" />
        </DialogContent>
      </Dialog>
    </section>
    </div>
  );
}
